// @mui
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
// routes
import { RouterLink } from 'src/routes/components';
// components
import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
// data
import { FOOTER_LINKS } from 'src/utils/data';

// ----------------------------------------------------------------------

export default function Footer() {
  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/cartifyapp?igsh=MWlxbW80Njhhc2tudA==', '_blank');
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/96181729436', '_blank');
  };

  const mainFooter = (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: 'background.default',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container
        sx={{
          pt: 4,
          pb: 3,
        }}
      >
        <Grid container spacing={3}>
          {/* Logo and Description */}
          <Grid xs={12} md={4}>
            <Logo sx={{ mb: 2, height: 32 }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                maxWidth: 280,
                lineHeight: 1.5,
              }}
            >
              Introducing Cartify - Your One-Stop Food and Beverage Supply App!
            </Typography>
          </Grid>

          {/* Navigation Links */}
          <Grid xs={12} md={5}>
            <Stack spacing={3} direction={{ xs: 'column', md: 'row' }}>
              {FOOTER_LINKS.map((list) => (
                <Stack key={list.headline} spacing={1.5} sx={{ minWidth: 140 }}>
                  <Typography
                    component="div"
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 1,
                    }}
                  >
                    {list.headline}
                  </Typography>

                  {list.children.map((link) => (
                    <Link
                      key={link.name}
                      component={RouterLink}
                      href={link.href}
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'primary.main',
                        },
                        fontSize: '0.875rem',
                      }}
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Grid>

          {/* Contact and Social */}
          <Grid xs={12} md={3}>
            <Stack spacing={2}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Contact & Social
              </Typography>

              <Stack spacing={1.5}>
                <Link
                  href="mailto:support@cartifyapp.ai"
                  color="text.secondary"
                  variant="body2"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    },
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Iconify icon="eva:email-fill" width={16} />
                  support@cartifyapp.ai
                </Link>

                <Link
                  href="https://wa.me/96181729436"
                  target="_blank"
                  color="text.secondary"
                  variant="body2"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    },
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Iconify icon="eva:phone-fill" width={16} />
                  +961 81 729436
                </Link>
              </Stack>

              {/* Social Icons */}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <IconButton
                  onClick={handleInstagramClick}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      color: '#E4405F',
                      bgcolor: 'rgba(228, 64, 95, 0.08)',
                      borderColor: '#E4405F',
                    },
                    width: 36,
                    height: 36,
                  }}
                >
                  <Iconify icon="mdi:instagram" width={20} />
                </IconButton>

                <IconButton
                  onClick={handleWhatsAppClick}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      color: '#25D366',
                      bgcolor: 'rgba(37, 211, 102, 0.08)',
                      borderColor: '#25D366',
                    },
                    width: 36,
                    height: 36,
                  }}
                >
                  <Iconify icon="mdi:whatsapp" width={20} />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Divider sx={{ my: 3 }} />
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          © 2025 Cartify. All rights reserved
        </Typography>
      </Container>
    </Box>
  );

  return mainFooter;
}
